import { useState, type ChangeEvent } from 'react';
import { Badge, Banner, Button, Dialog, Field, Select, Textarea, useAction, useInvalidateResource, type ScreenContext } from '../../engine';

// "Import CSV" for clients: paste or pick a CSV (read client-side), preview with
// POST /v1/clients/import { dry_run: true } (column mapping + dedupe + per-row results), then
// run the real import. The CSV text travels in the JSON body — no multipart route needed.

interface RowResult {
  row: number;
  action: 'create' | 'update' | 'skip' | 'error';
  name: string | null;
  match?: { name: string; by: string };
  errors?: string[];
}
interface ImportResult {
  dry_run: boolean;
  headers: string[];
  mapping: Record<string, string>;
  totals: { rows: number; create: number; update: number; skip: number; error: number };
  results: RowResult[];
}

const TONE: Record<RowResult['action'], 'pos' | 'info' | 'muted' | 'neg'> = { create: 'pos', update: 'info', skip: 'muted', error: 'neg' };

export function ImportClientsAction({ refresh }: ScreenContext & { selected?: string[] }) {
  const [open, setOpen] = useState(false);
  const [csv, setCsv] = useState('');
  const [onDuplicate, setOnDuplicate] = useState<'skip' | 'update'>('skip');
  const [preview, setPreview] = useState<ImportResult | null>(null);
  const invalidate = useInvalidateResource();
  const run = useAction<ImportResult, { csv: string; dry_run?: boolean; on_duplicate: string }>('clients', 'import', {
    toast: { success: (r) => { const t = (r as ImportResult).totals; return (r as ImportResult).dry_run ? `Preview: ${t.create} to create, ${t.update} to update, ${t.skip} skipped, ${t.error} errors` : `Imported ${t.create} new, ${t.update} updated`; } },
    onSuccess: (r) => {
      setPreview(r);
      if (!r.dry_run) {
        invalidate('clients');
        invalidate('contacts');
        refresh();
      }
    },
  });
  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result ?? ''));
    reader.readAsText(f);
  };
  const close = () => {
    setOpen(false);
    setPreview(null);
  };
  return (
    <>
      <Button icon="upload" onClick={() => setOpen(true)}>
        Import CSV
      </Button>
      <Dialog open={open} onClose={close} title="Import clients" description="First row = headers. Columns like Company, Website, Email, Contact, Phone, Industry, Type are mapped automatically; duplicates are matched by name, email domain or contact email." size="lg" locked={run.isPending}>
        <div className="hf-stack">
          <Field label="CSV file">
            <input type="file" accept=".csv,text/csv" onChange={onFile} aria-label="CSV file" />
          </Field>
          <Field label="Or paste CSV">
            <Textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={6} placeholder={'Company,Website,Email,Contact\nAcme Ltd,acme.com,cto@acme.com,Cara Jones'} aria-label="CSV text" className="hf-mono" />
          </Field>
          <Field label="When a row matches an existing client" inline>
            <Select size="sm" aria-label="On duplicate" value={onDuplicate} onChange={(e) => setOnDuplicate(e.target.value as 'skip' | 'update')} options={[{ value: 'skip', label: 'Skip it' }, { value: 'update', label: 'Update it' }]} />
          </Field>
          {preview && (
            <div className="hf-stack" style={{ gap: 6 }} data-testid="import-preview">
              <Banner tone={preview.totals.error ? 'warn' : 'info'}>
                {preview.dry_run ? 'Preview' : 'Imported'}: {preview.totals.create} create · {preview.totals.update} update · {preview.totals.skip} skip · {preview.totals.error} error
                <span className="hf-muted"> — mapped {Object.entries(preview.mapping).map(([k, v]) => `${v}→${k}`).join(', ')}</span>
              </Banner>
              <ul className="hf-import-rows" aria-label="Import rows">
                {preview.results.slice(0, 200).map((r) => (
                  <li key={r.row} className="hf-row" style={{ gap: 8 }}>
                    <span className="hf-mono hf-muted" style={{ width: 40 }}>#{r.row}</span>
                    <Badge tone={TONE[r.action]}>{r.action}</Badge>
                    <span className="hf-grow">{r.name ?? <span className="hf-muted">(no name)</span>}</span>
                    {r.match && <span className="hf-muted" style={{ fontSize: 'var(--t-12)' }}>matches {r.match.name || 'an earlier row'} by {r.match.by}</span>}
                    {r.errors?.length ? <span style={{ color: 'var(--neg)', fontSize: 'var(--t-12)' }}>{r.errors.join('; ')}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="hf-form-actions">
            <Button variant="ghost" onClick={close} disabled={run.isPending}>
              Close
            </Button>
            <Button icon="eye" busy={run.isPending} disabled={!csv.trim()} onClick={() => run.mutate({ body: { csv, dry_run: true, on_duplicate: onDuplicate } })}>
              Preview
            </Button>
            <Button variant="primary" icon="upload" busy={run.isPending} disabled={!csv.trim() || !preview?.dry_run} onClick={() => run.mutate({ body: { csv, on_duplicate: onDuplicate } })}>
              Import {preview?.dry_run ? `${preview.totals.create + preview.totals.update} rows` : ''}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
